import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { useAppSelector, useAppDispatch } from "@/hooks/redux";
import { applyCodeCorrection } from "@/store/slices/aiChatSlice";
import { cn } from "@/utils/cn";
import { Bot, User } from "lucide-react";
import { CreateCodeDialog } from "@/pages/dashboard/CreateCodeDialog";
import { createGeneratedCode } from "@/services/generatedCodeService";
import { aiCorrectionService } from "@/services/aiCorrectionService";
import { extractErrorMessage } from "@/types/generatedCode";
import { toast } from "sonner";
import { useAvailableTags } from "@/hooks/useAvailableTags";
import {
  createCodeValidationSchema,
  createCodeInitialValues,
} from "@/schemas/createCodeSchema";
import type { CreateCodeFormValues } from "@/types/generatedCode";
import type { SilaeFormData } from "@/types/silae";
import { SilaeCodeBlock } from "./SilaeCodeBlock";
import { AiValidationDiagram } from "./AiValidationDiagram";
import { ThinkingTimeline } from "./ThinkingTimeline";
import { FeedbackButtons } from "./FeedbackButtons";

// Typing indicator shown while the agent is streaming
const TypingIndicator: React.FC = () => (
  <div className="flex items-end gap-2.5">
    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 shrink-0">
      <Bot className="h-4 w-4 text-primary" />
    </div>
    <div className="bg-muted rounded-2xl rounded-bl-sm px-4 py-3">
      <div className="flex gap-1 items-center h-4">
        <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:0ms]" />
        <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:150ms]" />
        <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:300ms]" />
      </div>
    </div>
  </div>
);

// ── Micro Markdown renderer ──────────────────────────────────────────────────
// Handles the subset the orchestrator produces: **bold**, `inline code`,
// bullet lists (- item), blockquotes (> text), and paragraphs.
// Avoids adding a heavy dependency for a single component.

function renderInline(text: string, key: string | number): React.ReactNode {
  // Split on **bold** and `code` spans
  const tokens = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return (
    <React.Fragment key={key}>
      {tokens.map((tok, j) => {
        if (tok.startsWith("**") && tok.endsWith("**"))
          return <strong key={j} className="font-semibold text-foreground">{tok.slice(2, -2)}</strong>;
        if (tok.startsWith("`") && tok.endsWith("`"))
          return <code key={j} className="rounded px-1 py-0.5 bg-muted font-mono text-[11px] text-foreground/80">{tok.slice(1, -1)}</code>;
        return tok;
      })}
    </React.Fragment>
  );
}

function renderMarkdownBlock(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  // Split into paragraphs (double newline) first
  const paragraphs = text.split(/\n{2,}/);

  paragraphs.forEach((para, pi) => {
    const lines = para.split("\n");
    const bulletLines = lines.filter((l) => /^[-*]\s+/.test(l.trim()));
    const quoteLine = lines.find((l) => l.trim().startsWith("> "));

    if (bulletLines.length === lines.filter((l) => l.trim()).length && bulletLines.length > 0) {
      // Pure bullet list paragraph
      nodes.push(
        <ul key={`ul-${pi}`} className="space-y-1 pl-1">
          {bulletLines.map((l, li) => (
            <li key={li} className="flex items-start gap-2 text-sm leading-relaxed">
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary/60 shrink-0" aria-hidden />
              <span>{renderInline(l.replace(/^[-*]\s+/, ""), `li-${pi}-${li}`)}</span>
            </li>
          ))}
        </ul>
      );
    } else if (quoteLine) {
      // Blockquote (warning note)
      const quoteText = lines
        .filter((l) => l.trim().startsWith("> "))
        .map((l) => l.replace(/^>\s?/, ""))
        .join(" ");
      nodes.push(
        <div key={`bq-${pi}`} className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/20 px-3 py-2.5">
          <span className="mt-0.5 text-amber-600 dark:text-amber-400 shrink-0 text-sm">⚠️</span>
          <p className="text-xs leading-relaxed text-amber-800 dark:text-amber-200">
            {renderInline(quoteText, `bq-${pi}`)}
          </p>
        </div>
      );
    } else if (para.trim()) {
      // Regular paragraph
      nodes.push(
        <p key={`p-${pi}`} className="text-sm leading-relaxed text-foreground/90">
          {renderInline(para.trim(), `p-${pi}`)}
        </p>
      );
    }
  });

  return nodes;
}

// Render assistant message content with Markdown styling.
// Text after the silae code block is intentionally dropped — it would appear
// between the code editor and the diagram, which creates visual clutter.
const MessageContent: React.FC<{
  content: string;
  onSave?: () => void;
  onCorrect?: (original: string, final: string) => void | Promise<void>;
}> = ({ content, onSave, onCorrect }) => {
  const FENCE_RE = /(```[\w-]*\n[\s\S]*?```)/g;
  const rawParts = content.split(FENCE_RE);

  const isSilaeFence = (p: string) => {
    const m = p.match(/^```([\w-]*)\n/);
    return Boolean(m && (m[1].toLowerCase() === "silae" || m[1] === ""));
  };

  // L'orchestrateur émet ses notes de clôture (rubriques utilisées, directives
  // admin — ex. phrase de fin) APRÈS le bloc de code. On déplace le bloc SILAE à
  // la fin pour que TOUT le texte (explication + directives) s'affiche AVANT le
  // code, puis le code, puis le diagramme. Placement déterministe, indépendant du modèle.
  const rawSilaeIdx = rawParts.findIndex(isSilaeFence);
  const parts =
    onSave && rawSilaeIdx !== -1
      ? [...rawParts.filter((_, i) => i !== rawSilaeIdx), rawParts[rawSilaeIdx]]
      : rawParts;

  // Index du bloc SILAE après réordonnancement (désormais en dernier).
  const silaeIdx = parts.findIndex(isSilaeFence);

  const cutoff = onSave && silaeIdx !== -1 ? silaeIdx : parts.length - 1;

  return (
    <div className="space-y-3">
      {parts.map((part, i) => {
        // Après le bloc SILAE principal : on garde le TEXTE (rubriques utilisées,
        // note variables, directives admin — ex. phrase de fin) et on ne masque
        // que d'éventuels blocs de code/JSON en double (le diagramme remplace le JSON).
        if (onSave && silaeIdx !== -1 && i > silaeIdx) {
          if (/^```/.test(part.trimStart())) return null;
        }

        const fenceMatch = part.match(/^```([\w-]*)\n([\s\S]*?)```$/);
        if (fenceMatch) {
          const lang = fenceMatch[1].toLowerCase();
          const code = fenceMatch[2].trimEnd();

          // JSON block → always hidden (diagram replaces it)
          if (lang === "json") return null;

          // SILAE block → rich editor
          if (lang === "silae" || lang === "") {
            return (
              <SilaeCodeBlock
                key={i}
                code={code}
                lang={lang || "silae"}
                onSave={i <= cutoff ? onSave : undefined}
                onCorrect={
                  onCorrect ? (final) => onCorrect(code, final) : undefined
                }
              />
            );
          }

          // Other languages → compact block
          return (
            <div key={i} className="rounded-lg overflow-hidden border border-border/60">
              {lang && (
                <div className="flex items-center px-3 py-1 bg-muted/80 border-b border-border/60">
                  <span className="text-[10px] font-mono font-medium text-muted-foreground uppercase tracking-wider">{lang}</span>
                </div>
              )}
              <pre className="text-xs overflow-x-auto whitespace-pre-wrap wrap-break-word font-mono bg-muted/40 px-3 py-2.5 leading-relaxed">
                <code>{code}</code>
              </pre>
            </div>
          );
        }

        // Plain text — render with Markdown
        if (!part.trim()) return null;
        return (
          <div key={i} className="space-y-2.5">
            {renderMarkdownBlock(part)}
          </div>
        );
      })}
    </div>
  );
};

export const MessageList: React.FC = () => {
  const { t } = useTranslation();
  const messages = useAppSelector((state) => state.aiChat.messages);
  const isStreaming = useAppSelector((state) => state.aiChat.isStreaming);
  const formData = useAppSelector((state) => state.aiChat.formData);
  const generatedCode = useAppSelector((state) => state.aiChat.generatedCode);
  const suggestedName = useAppSelector((state) => state.aiChat.suggestedName);
  const script = useAppSelector((state) => state.aiChat.script);
  const codeManuallyEdited = useAppSelector(
    (state) => state.aiChat.codeManuallyEdited,
  );
  const dispatch = useAppDispatch();

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const { allTags } = useAvailableTags();

  // Correction d'un code généré par l'IA : on envoie le diff (généré → corrigé).
  // Le serveur ne retient que les corrections des utilisateurs de confiance pour
  // l'apprentissage ; pour les autres, c'est enregistré mais sans effet.
  const handleCorrect = React.useCallback(
    async (original: string, final: string) => {
      // 1) On adopte la version corrigée partout (bloc affiché, copie, sauvegarde).
      dispatch(applyCodeCorrection(final));
      // 2) On enregistre le diff pour l'apprentissage (sans effet si user non de confiance).
      await aiCorrectionService.record({
        generatedCode: original,
        finalCode: final,
        primeName: suggestedName ?? null,
      });
    },
    [suggestedName, dispatch],
  );

  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Scroll the messages container only — never the page.
  // Using scrollIntoView() would bubble up to the nearest scrollable ancestor
  // (incl. document) and shift the whole page upward when the ClarificationPanel
  // appears and pushes the sentinel out of the viewport.
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const formik = useFormik<CreateCodeFormValues>({
    initialValues: createCodeInitialValues,
    validationSchema: createCodeValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: false,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (!formData && !script) return;
      const savedFormData: SilaeFormData = formData ?? {
        structureType: "simple",
        hasExclusions: false,
        exclusions: [],
        exclusionsOperators: [],
        conditions: [],
        conditionsOperators: [],
        ifBranch: { bonuses: [] },
      };
      try {
        const newCode = await createGeneratedCode({
          name: values.name.trim(),
          description: values.description.trim() || undefined,
          formData: savedFormData,
          generatedCode: formData ? (generatedCode ?? "") : (script ?? ""),
          // Mode B (script) OU code corrigé à la main → on stocke le code tel quel.
          // Sans ça, en Mode A le serveur régénère depuis formData et perd la correction.
          bypassCodeGeneration: (!formData && !!script) || codeManuallyEdited,
          tags: values.tags,
          isTemplate: false,
        });
        toast.success(t("aiAgent.save.savedSuccess", { name: newCode.name }));
        setSaveDialogOpen(false);
        resetForm();
      } catch (error: unknown) {
        toast.error(extractErrorMessage(error, t("aiAgent.save.errorSave")));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleOpenSaveDialog = () => {
    formik.resetForm({
      values: {
        name: suggestedName ?? "",
        description: "",
        tags: [],
      },
    });
    setSaveDialogOpen(true);
  };

  const lastAssistantIndex = messages.reduceRight(
    (found, msg, idx) => (found === -1 && msg.role === "assistant" ? idx : found),
    -1,
  );

  const showSaveButton = (formData || script) && !isStreaming;

  return (
    <div ref={containerRef} className={cn("flex-1 min-h-0 bg-background p-4 space-y-4", messages.length === 0 ? "overflow-y-hidden" : "overflow-y-auto")}>
      {messages.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-6">
          <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-primary/10">
            <Bot className="h-6 w-6 text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">{t("aiAgent.emptyState.title")}</p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {t("aiAgent.emptyState.description")}
              <br />
              {t("aiAgent.emptyState.hint")}
            </p>
          </div>
        </div>
      ) : (
        messages.map((message, idx) => {
          const isUser = message.role === "user";
          const isLastAssistant = idx === lastAssistantIndex;

          return (
            <div
              key={message.id}
              className={cn(
                "flex items-end gap-2.5",
                isUser ? "flex-row-reverse" : "flex-row",
              )}
            >
              {/* Avatar */}
              <div
                className={cn(
                  "flex items-center justify-center w-7 h-7 rounded-full shrink-0",
                  isUser ? "bg-primary" : "bg-primary/10",
                )}
              >
                {isUser ? (
                  <User className="h-3.5 w-3.5 text-primary-foreground" />
                ) : (
                  <Bot className="h-3.5 w-3.5 text-primary" />
                )}
              </div>

              {/* Bubble */}
              <div
                className={cn(
                  "flex flex-col gap-1.5",
                  isUser ? "items-end" : "items-start",
                  "w-full max-w-[82%]",
                )}
              >
                {/* Timeline de raisonnement (style Claude Code), au-dessus de la bulle */}
                {!isUser && message.thinking && message.thinking.length > 0 && (
                  <ThinkingTimeline
                    steps={message.thinking}
                    isStreaming={isStreaming && isLastAssistant}
                    durationMs={message.thinkingDurationMs}
                  />
                )}

                {/* Bulle de texte — masquée si l'assistant n'a encore produit aucun texte */}
                {(isUser || message.content.trim().length > 0) && (
                  <div
                    className={cn(
                      "w-full px-4 py-3 rounded-2xl text-sm shadow-xs",
                      isUser
                        ? "bg-primary text-primary-foreground rounded-br-sm"
                        : "bg-muted text-foreground rounded-bl-sm",
                    )}
                  >
                    {isUser ? (
                      <p className="whitespace-pre-wrap wrap-break-word leading-relaxed text-sm">
                        {message.content}
                      </p>
                    ) : (
                      <>
                        <MessageContent
                          content={message.content}
                          onSave={isLastAssistant && showSaveButton ? handleOpenSaveDialog : undefined}
                          onCorrect={handleCorrect}
                        />
                        {message.formData && (!isStreaming || !isLastAssistant) && (
                          <AiValidationDiagram formData={message.formData} />
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Évaluation 👍/👎 — uniquement sur les réponses terminées */}
                {!isUser &&
                  message.content.trim().length > 0 &&
                  (!isStreaming || !isLastAssistant) && (
                    <FeedbackButtons messageId={message.id} />
                  )}
              </div>
            </div>
          );
        })
      )}

      {/* Typing indicator — uniquement au tout début, avant l'apparition de la
          timeline de raisonnement (qui prend ensuite le relais de l'animation). */}
      {isStreaming &&
        (() => {
          const last = messages[messages.length - 1];
          const hasActiveTimeline =
            last?.role === "assistant" && (last.thinking?.length ?? 0) > 0;
          return hasActiveTimeline ? null : <TypingIndicator />;
        })()}

      <div ref={messagesEndRef} />

      <CreateCodeDialog
        open={saveDialogOpen}
        onOpenChange={(open) => {
          setSaveDialogOpen(open);
          if (!open) formik.resetForm();
        }}
        formik={formik}
        allTags={allTags}
      />
    </div>
  );
};
